import { useState, useEffect } from "react";
import { apiClient } from "@/App";
import { toast } from "sonner";
import { 
  Activity, Plus, Trash2, X, Loader2, MapPin, Users, 
  FileText, CheckCircle, Calendar, MessageSquare, DollarSign,
  Clock, Edit, Pencil
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const activityTypes = [
  { value: "site_visit", label: "Site Visit", icon: MapPin, color: "bg-blue-500" },
  { value: "meeting", label: "Meeting", icon: Users, color: "bg-purple-500" },
  { value: "milestone", label: "Milestone", icon: CheckCircle, color: "bg-green-500" },
  { value: "update", label: "Update", icon: Activity, color: "bg-yellow-500" },
  { value: "note", label: "Note", icon: MessageSquare, color: "bg-gray-500" },
  { value: "payment", label: "Payment", icon: DollarSign, color: "bg-emerald-500" },
  { value: "document", label: "Document", icon: FileText, color: "bg-orange-500" }
];

const getActivityIcon = (type) => {
  const activityType = activityTypes.find(t => t.value === type);
  return activityType || activityTypes.find(t => t.value === "update");
};

export default function ActivityLog({ projectId }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [filter, setFilter] = useState("all");
  const [editingActivity, setEditingActivity] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [newActivity, setNewActivity] = useState({
    activity_type: "update",
    title: "",
    description: "",
    date: new Date().toISOString().split("T")[0]
  });

  useEffect(() => {
    fetchActivities();
  }, [projectId, filter]);

  const fetchActivities = async () => {
    try {
      const url = filter === "all"
        ? `/projects/${projectId}/activities`
        : `/projects/${projectId}/activities?activity_type=${filter}`;
      const response = await apiClient.get(url);
      setActivities(response.data);
    } catch (error) {
      console.error("Error fetching activities:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newActivity.title) {
      toast.error("Please enter a title");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post(`/projects/${projectId}/activities`, {
        project_id: projectId,
        ...newActivity
      });
      toast.success("Activity logged");
      setNewActivity({
        activity_type: "update",
        title: "",
        description: "",
        date: new Date().toISOString().split("T")[0]
      });
      setShowAdd(false);
      fetchActivities();
    } catch (error) {
      toast.error("Failed to log activity");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (activity) => {
    setEditingActivity({
      ...activity,
      activity_type: activity.activity_type,
      title: activity.title,
      description: activity.description || "",
      date: activity.date
    });
  };

  const handleUpdate = async () => {
    if (!editingActivity.title) {
      toast.error("Please enter a title");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.put(`/projects/${projectId}/activities/${editingActivity.activity_id}`, {
        activity_type: editingActivity.activity_type,
        title: editingActivity.title,
        description: editingActivity.description,
        date: editingActivity.date
      });
      toast.success("Activity updated");
      setEditingActivity(null);
      fetchActivities();
    } catch (error) {
      toast.error("Failed to update activity");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (activityId) => {
    if (!confirm("Delete this activity?")) return;
    try {
      await apiClient.delete(`/projects/${projectId}/activities/${activityId}`);
      toast.success("Activity deleted");
      fetchActivities();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="activity-log">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[140px] bg-[#1A1A1A] border-[#262626] text-sm">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Activities</SelectItem>
              {activityTypes.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-gray-500">{activities.length} entries</span>
        </div>
        <Button 
          size="sm" 
          className="bg-[#FF4500] hover:bg-[#E65100]"
          onClick={() => setShowAdd(true)}
          data-testid="log-activity-btn"
        >
          <Plus className="w-4 h-4 mr-1" />
          Log Activity
        </Button>
      </div>

      {/* Timeline */}
      {activities.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-500">
          <Activity className="w-12 h-12 mb-3 opacity-50" />
          <p className="text-sm">No activities logged yet</p>
          <p className="text-xs mt-1">Track your project progress here</p>
        </div>
      ) : (
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-4 top-0 bottom-0 w-px bg-[#262626]" />
          
          <div className="space-y-4">
            {activities.map((activity, index) => {
              const typeInfo = getActivityIcon(activity.activity_type);
              const Icon = typeInfo.icon;
              
              return (
                <div key={activity.activity_id} className="relative flex gap-4 pl-10">
                  {/* Icon */}
                  <div className={`absolute left-0 w-8 h-8 rounded-full ${typeInfo.color} flex items-center justify-center`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 bg-[#1A1A1A] border border-[#262626] rounded-lg p-4 group hover:border-[#404040] transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-[10px] uppercase tracking-wider text-gray-500">
                            {typeInfo.label}
                          </span>
                          <span className="text-[10px] text-gray-600">•</span>
                          <span className="text-[10px] text-gray-500 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {activity.date}
                          </span>
                          {activity.edited_at && (
                            <>
                              <span className="text-[10px] text-gray-600">•</span>
                              <span className="text-[10px] text-amber-500 flex items-center gap-1" title={`Edited by ${activity.edited_by_name || 'Unknown'}`}>
                                <Pencil className="w-3 h-3" />
                                Edited {new Date(activity.edited_at).toLocaleDateString()}
                              </span>
                            </>
                          )}
                        </div>
                        <h4 className="text-white font-medium text-sm">{activity.title}</h4>
                        {activity.description && (
                          <p className="text-gray-400 text-xs mt-2 leading-relaxed whitespace-pre-wrap">
                            {activity.description}
                          </p>
                        )}
                        {activity.created_by_name && (
                          <p className="text-[10px] text-gray-600 mt-2">
                            by {activity.created_by_name}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleEdit(activity)}
                          className="text-gray-500 hover:text-blue-400 p-1"
                          title="Edit"
                          data-testid={`edit-activity-${activity.activity_id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(activity.activity_id)}
                          className="text-gray-500 hover:text-red-500 p-1"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Add Activity Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1A1A1A] border border-[#262626] rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Log Activity</h3>
              <button onClick={() => setShowAdd(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-400">Type</Label>
                  <Select 
                    value={newActivity.activity_type} 
                    onValueChange={(v) => setNewActivity({ ...newActivity, activity_type: v })}
                  >
                    <SelectTrigger className="bg-[#171717] border-[#262626]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {activityTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <span className="flex items-center gap-2">
                            <type.icon className="w-3 h-3" />
                            {type.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-gray-400">Date</Label>
                  <Input
                    type="date"
                    value={newActivity.date}
                    onChange={(e) => setNewActivity({ ...newActivity, date: e.target.value })}
                    className="bg-[#171717] border-[#262626]"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-400">Title *</Label>
                <Input
                  value={newActivity.title}
                  onChange={(e) => setNewActivity({ ...newActivity, title: e.target.value })}
                  placeholder="e.g., Site inspection completed"
                  className="bg-[#171717] border-[#262626]"
                  data-testid="activity-title-input"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-400">Description (optional)</Label>
                <Textarea
                  value={newActivity.description}
                  onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })}
                  placeholder="Add details about this activity..."
                  className="bg-[#171717] border-[#262626] min-h-[80px]"
                  data-testid="activity-description-input"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1 border-[#262626]" onClick={() => setShowAdd(false)}>
                  Cancel
                </Button>
                <Button className="flex-1 bg-[#FF4500] hover:bg-[#E65100]" onClick={handleAdd} disabled={submitting}>
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log Activity"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Activity Modal */}
      {editingActivity && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1A1A1A] border border-[#262626] rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Edit Activity</h3>
              <button onClick={() => setEditingActivity(null)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-400">Type</Label>
                  <Select 
                    value={editingActivity.activity_type} 
                    onValueChange={(v) => setEditingActivity({ ...editingActivity, activity_type: v })}
                  >
                    <SelectTrigger className="bg-[#171717] border-[#262626]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {activityTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <span className="flex items-center gap-2">
                            <type.icon className="w-3 h-3" />
                            {type.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-gray-400">Date</Label>
                  <Input
                    type="date"
                    value={editingActivity.date}
                    onChange={(e) => setEditingActivity({ ...editingActivity, date: e.target.value })}
                    className="bg-[#171717] border-[#262626]"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-400">Title *</Label>
                <Input
                  value={editingActivity.title}
                  onChange={(e) => setEditingActivity({ ...editingActivity, title: e.target.value })}
                  placeholder="e.g., Site inspection completed"
                  className="bg-[#171717] border-[#262626]"
                  data-testid="edit-activity-title"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-400">Description (optional)</Label>
                <Textarea
                  value={editingActivity.description}
                  onChange={(e) => setEditingActivity({ ...editingActivity, description: e.target.value })}
                  placeholder="Add details about this activity..."
                  className="bg-[#171717] border-[#262626] min-h-[80px]"
                  data-testid="edit-activity-description"
                />
              </div>
              <p className="text-xs text-amber-500 flex items-center gap-1">
                <Pencil className="w-3 h-3" />
                This edit will be timestamped
              </p>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1 border-[#262626]" onClick={() => setEditingActivity(null)}>
                  Cancel
                </Button>
                <Button className="flex-1 bg-[#FF4500] hover:bg-[#E65100]" onClick={handleUpdate} disabled={submitting}>
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
